import React, { useContext } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Field } from "formik";
import { TextField as TextFieldUI } from "@material-ui/core";
import useStyles from "./useStyles";
import MaskedInput from "react-text-mask";
import NumberFormat from "react-number-format";
import { masks as Masks } from "../../Input/Input";
import AppContext from "AppContext";
import currencies from "utils/static/currencies";

function TextField({
    className,
    fieldName,
    type,
    helperText,
    capitalize,
    uppercase,
    lowercase,
    mask,
    inputProps,
    currency,
    numeric,
    onChange,
    ...rest
}) {
    const classes = useStyles();

    function TextMaskCustom(props) {
        const { inputRef, ...other } = props;

        return (
            <MaskedInput
                {...other}
                ref={(ref) => {
                    inputRef(ref ? ref.inputElement : null);
                }}
                mask={mask.mask}
                placeholderChar={mask.char}
            />
        );
    }

    function NumberFormatCustom(props) {
        const { inputRef, onChange, ...other } = props;
        const { configurations } = useContext(AppContext);
        const currencyConfig = configurations.find((config) => config.id === "general_currency");
        const currencyIsoCode = currencyConfig?.value;
        const currencyObj = currencies.find((c) => c.isoCode === currencyIsoCode);
        const currencySymbol = currency ? `${currencyObj?.symbol || "$"} ` : "";

        return (
            <NumberFormat
                {...other}
                getInputRef={inputRef}
                onValueChange={(values) => {
                    onChange({
                        target: {
                            name: props.name,
                            value: values.value,
                        },
                    });
                }}
                thousandSeparator
                isNumericString
                prefix={currencySymbol}
            />
        );
    }

    NumberFormatCustom.propTypes = {
        inputRef: PropTypes.func.isRequired,
        name: PropTypes.string.isRequired,
        onChange: PropTypes.func.isRequired,
    };

    function generateInputProps() {
        let inputComponent = undefined;
        if (mask) inputComponent = TextMaskCustom;
        if (currency || numeric) inputComponent = NumberFormatCustom;
        return { ...inputProps, inputComponent };
    }

    const onChangeHandler = (event, field, form) => {
        field.onChange(event);
        if (onChange && typeof onChange === "function") {
            onChange(field.name, event && event.target && event.target.value, form);
        }
    };

    return (
        <Field type={type} name={fieldName}>
            {({ field, form }) => (
                <TextFieldUI
                    {...field}
                    {...rest}
                    onChange={(e) => onChangeHandler(e, field, form)}
                    type={type}
                    helperText={form.touched[fieldName] ? form.errors[fieldName] : helperText}
                    error={Boolean(form.touched[field.name]) && Boolean(form.errors[field.name])}
                    className={clsx(className, {
                        [classes.capitalize]: capitalize,
                        [classes.uppercase]: uppercase,
                        [classes.lowercase]: lowercase,
                    })}
                    InputProps={generateInputProps()}
                />
            )}
        </Field>
    );
}

TextField.propTypes = {
    fieldName: PropTypes.string.isRequired,
    type: PropTypes.string,
    variant: PropTypes.string,
    size: PropTypes.string,
    margin: PropTypes.string,
    autoComplete: PropTypes.string,
    fullWidth: PropTypes.bool,
    helperText: PropTypes.string,
    capitalize: PropTypes.bool,
    uppercase: PropTypes.bool,
    lowercase: PropTypes.bool,
    mask: PropTypes.oneOf([Masks.ssn, Masks.alienRegistrationNo, Masks.phoneNumber, Masks.twoDigits]),
    inputProps: PropTypes.shape({}),
    numeric: PropTypes.bool,
    onChange: PropTypes.func,
};

TextField.defaultProps = {
    type: "text",
    variant: "outlined",
    size: "small",
    margin: "normal",
    autoComplete: "off",
    fullWidth: true,
    helperText: undefined,
    capitalize: false,
    uppercase: false,
    lowercase: false,
    mask: undefined,
    inputProps: undefined,
    numeric: false,
    onChange: undefined,
};

export default TextField;
